import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import relationships from 'Consts/Relationships';
import {
  REAL_ASSET_TYPE_PROPERTY, REAL_ASSET_TYPE_PERSONAL_ASSET, REAL_ASSET_TYPE_VEHICLE, REAL_ASSET_TYPE_BUSINESS,
} from 'Consts/RealAssets';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { dispatchAccountsRefresh } from 'Hooks/useRefreshEvent';
import {
  PropertyOverride, PersonalAssetOverride, VehicleOverride, BusinessOwnershipOverride, NewRealAssetOverride,
} from 'Resources/QuestionnaireRealAssets';
import MetaCollectionFormDialog from 'Components/UI/Meta/MetaForm/MetaCollectionFormDialog';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';
import closeModal from 'Helpers/ModalHelper';
import { has } from 'lodash';

const TYPES_TO_META_OVERRIDES = {};
TYPES_TO_META_OVERRIDES[REAL_ASSET_TYPE_PROPERTY] = PropertyOverride;
TYPES_TO_META_OVERRIDES[REAL_ASSET_TYPE_PERSONAL_ASSET] = PersonalAssetOverride;
TYPES_TO_META_OVERRIDES[REAL_ASSET_TYPE_VEHICLE] = VehicleOverride;
TYPES_TO_META_OVERRIDES[REAL_ASSET_TYPE_BUSINESS] = BusinessOwnershipOverride;

function RealAssetModal({
  id,
  type,
  withDelete,
}) {
  const { collection, setCollection } = useCollection();
  const { householdResource } = useSimpleHousehold();

  const handleClose = () => {
    closeModal();
  };

  const onSuccess = () => {
    dispatchAccountsRefresh();
    displaySuccess('Real Asset Saved');
    handleClose();
  };

  const onDelete = () => {
    dispatchAccountsRefresh();
    displaySuccess('Real Asset Deleted');
    handleClose();
  };

  const onFailure = (err) => {
    displayErrorMessage(err, err.errors);
  };

  useEffect(() => {
    if (!collection && householdResource && householdResource.relationship(relationships.realAssets)) {
      const getParams = { params: id ? { id } : { limit: 0, offset: 0 } };
      if (type) {
        if (!has(TYPES_TO_META_OVERRIDES, type)) {
          displayErrorMessage(new Error(), `Unknown Real Asset Type: ${type}`);
          handleClose();
          return;
        }
        getParams.metaOverrideSet = TYPES_TO_META_OVERRIDES[type];
      }

      householdResource
        .relationship(relationships.realAssets)
        .link('related')
        .Get(getParams)
        .then((coll) => {
          if (coll.resources().length() === 0) {
            //  Create a new generic resource with a meta override that compensates for us not knowing a specific type yet.
            coll.create({
              metaOverrideSet: NewRealAssetOverride,
              addToCollection: true,
            });
            setCollection(coll);
          } else {
            //  Override our generic resource's meta to match the specific returned type.
            const fetchedRealAssetType = coll.resources().head().attr('type');
            if (has(TYPES_TO_META_OVERRIDES, fetchedRealAssetType)) {
              setCollection(coll.overrideMeta(TYPES_TO_META_OVERRIDES[fetchedRealAssetType]));
            } else {
              //  If the resource has a type the client doesn't recognize, just show it as is.
              setCollection(coll);
            }
          }
        })
        .catch((err) => {
          displayErrorMessage(err, err.errors);
        });
    }
  }, [householdResource]);

  if (!collection) return null;

  return (
    <MetaCollectionFormDialog
      collection={collection}
      onClose={handleClose}
      onFailure={onFailure}
      onSubmit={onSuccess}
      onDelete={id && withDelete ? onDelete : null}
      title="Edit Real Asset"
    />
  );
}

RealAssetModal.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  withDelete: PropTypes.bool,
};
RealAssetModal.defaultProps = {
  withDelete: false,
};

export default RealAssetModal;
